/**
 * OutcomesOverviewV2 — the Hired Candidates ledger (Outcomes list surface).
 *
 * ── What changed and why ─────────────────────────────────────────────────────
 * This page used to be a read-only report over `GET /outcomes`, which is driven
 * by ``candidate_outcomes`` rows. A candidate moved to Hired in the pipeline had
 * no such row until somebody filled in the (now-removed) capture form on the
 * candidate page, so the hires that needed data were the exact rows the page
 * could not show. Nothing prompted anyone, nothing got filled in, the page read
 * blank.
 *
 * It is now a LEDGER over `GET /outcomes/hired`: every hired candidate appears
 * automatically, every analytics variable is editable in place, and a
 * completeness count plus a "Needs data" filter make the backlog finite and
 * visible. Capture happens where the gap is visible instead of on a form
 * somebody has to remember to open.
 *
 * ── Founder-set interaction rules ────────────────────────────────────────────
 *   • The candidate's NAME is the only navigating element (→ candidate page).
 *     The row does not navigate: on a grid built for filling cells, a row-level
 *     click target throws you off the page mid-edit.
 *   • Inline edit, save on blur. No modal, no Save button.
 *   • Optimistic with rollback, and a VISIBLE error on failure — an edit is
 *     never silently dropped.
 *   • `/outcomes/:candidateId` stays reachable, as a small secondary row
 *     affordance rather than the row's primary action.
 *
 * ── Data honesty ─────────────────────────────────────────────────────────────
 * `GET /outcomes/hired` is a per-user-scoped PAGINATED list with NO tenant-wide
 * aggregate field. The KPIs are therefore derived client-side over the loaded
 * page — EXACT when the page is complete (``has_more === false``, the common
 * case), a lower bound otherwise (the footnote says so). No trend/prior data
 * exists, so the KpiCard delta badges are suppressed and the sparklines draw an
 * honest flat line ([]) — never a fabricated wiggle. Every metric renders an
 * em-dash ("—") when its denominator is empty.
 *
 * ── Token discipline ─────────────────────────────────────────────────────────
 * No raw hex/rgb. Fit-band + verdict chips use the fit-band HSL-triple set
 * (--strong / --moderate / --weak / --brand + their -soft bgs). Surface/text
 * semantics use the direct token utilities.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { JSX } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTenantBase } from "@/hooks/useTenantBase";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useAuthStore } from "@/stores/useAuthStore";
import { useToastStore } from "@/stores/useToastStore";
import { cn } from "@/lib/utils";
import { KpiCard } from "@/components/v2/KpiCard";
import { Skeleton } from "@/components/ui/skeleton";
import { useTeamUsers } from "@/hooks/queries/users/useTeamUsers";
import { useMe } from "@/hooks/queries/auth/useMe";
import {
  useHiredLedger,
  useLedgerCellPatch,
  type HiredLedgerItem,
  type HiredLedgerSort,
  type SortDirection,
} from "@/hooks/queries/outcomes/useHiredLedger";
import { HiredLedgerGrid } from "@/components/v2/outcomes/HiredLedgerGrid";
import { HiredLedgerStack } from "@/components/v2/outcomes/HiredLedgerStack";
import type {
  LedgerColumnGroup,
  LedgerFieldKey,
} from "@/components/v2/outcomes/ledgerFields";

// The grid needs ~13 columns of room. Below this we fall back to the
// list + detail-panel shape (one candidate at a time) rather than making a
// phone user drag a 13-column grid sideways.
const GRID_MIN_WIDTH = 900;

const SORT_KEYS = new Set<string>([
  "hired",
  "candidate",
  "role",
  "completeness",
  "submitted_to_client_at",
  "client_callback_at",
  "offer_extended_at",
  "placed_at",
  "hired_at",
  "start_date",
  "updated_at",
]);

// ── KPI helpers ────────────────────────────────────────────────────────────────
/** Whole-percent of `num`/`den`, or null when the denominator is empty. */
function pct(num: number, den: number): number | null {
  return den > 0 ? Math.round((num / den) * 100) : null;
}
/** Renders an em-dash rather than a misleading 0 when there is nothing to divide. */
function pctStr(v: number | null): string {
  return v == null ? "—" : `${v}`;
}

interface LedgerKpis {
  accuracy: number | null;
  gradedCount: number;
  tracked: number;
  retention: number | null;
  rehire: number | null;
  needsData: number;
}

function computeKpis(items: HiredLedgerItem[]): LedgerKpis {
  let graded = 0;
  let confirmed = 0;
  let retentionDen = 0;
  let retained = 0;
  let rehireDen = 0;
  let rehired = 0;
  let needsData = 0;

  for (const r of items) {
    // Accuracy = confirmed calls / all graded (terminal, non-pending) calls.
    if (r.verdict === "confirmed" || r.verdict === "over_called" || r.verdict === "under_called") {
      graded += 1;
      if (r.verdict === "confirmed") confirmed += 1;
    }
    if (r.still_employed_at_180d !== null) {
      retentionDen += 1;
      if (r.still_employed_at_180d === true) retained += 1;
    }
    if (r.would_rehire !== null) {
      rehireDen += 1;
      if (r.would_rehire === true) rehired += 1;
    }
    if (r.completeness < r.completeness_total) needsData += 1;
  }

  return {
    accuracy: pct(confirmed, graded),
    gradedCount: graded,
    tracked: items.length,
    retention: pct(retained, retentionDen),
    rehire: pct(rehired, rehireDen),
    needsData,
  };
}

// ── Loading skeleton ───────────────────────────────────────────────────────────
function LedgerLoading(): JSX.Element {
  return (
    <div>
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[16px]"
        style={{ marginBottom: "16px" }}
      >
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-[132px] w-full rounded-[var(--radius)]" />
        ))}
      </div>
      <Skeleton className="h-[320px] w-full rounded-[var(--radius)]" />
    </div>
  );
}

// ── States card (empty / error) ────────────────────────────────────────────────
function StateCard({
  title,
  body,
  testId,
}: {
  title: string;
  body: string;
  testId?: string;
}): JSX.Element {
  return (
    <div
      className="flex flex-col items-center justify-center rounded-[var(--radius)] border border-border-default bg-surface-card px-4 text-center shadow-[var(--shadow-sm)]"
      style={{ paddingTop: "48px", paddingBottom: "48px" }}
      data-testid={testId}
    >
      <h3 className="text-[15px] font-semibold text-text-primary">{title}</h3>
      <p className="mt-1 max-w-md text-[13px] text-text-secondary">{body}</p>
    </div>
  );
}

// ── Column-group toggle ────────────────────────────────────────────────────────

const GROUP_OPTIONS: Array<{ value: LedgerColumnGroup; label: string }> = [
  { value: "all", label: "All" },
  { value: "agency", label: "Agency" },
  { value: "employment", label: "Employment" },
];

function GroupToggle({
  value,
  onChange,
  deEmphasiseAgency,
}: {
  value: LedgerColumnGroup;
  onChange: (v: LedgerColumnGroup) => void;
  deEmphasiseAgency: boolean;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Which outcome fields to show"
      data-testid="v2-outcomes-group-toggle"
      className="inline-flex h-9 items-center rounded-[var(--radius)] border border-border-default bg-surface-card p-[2px]"
    >
      {GROUP_OPTIONS.map((opt) => {
        const active = opt.value === value;
        // In-house tenants still get the Agency group — de-emphasised, never
        // deleted. "Submitted to client" is agency vocabulary, but an in-house
        // team may genuinely use it, and hiding data someone already recorded
        // is worse than showing a group they ignore.
        const dim = deEmphasiseAgency && opt.value === "agency" && !active;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            title={
              dim
                ? "Agency-side fields. Your tenant is set up as in-house, so these are optional."
                : undefined
            }
            data-testid={`v2-outcomes-group-${opt.value}`}
            className={cn(
              "h-full rounded-[calc(var(--radius)-2px)] px-[10px] text-[12.5px] transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus",
              active
                ? "bg-surface-subtle text-text-primary"
                : "text-text-secondary hover:text-text-primary",
              dim && "opacity-55",
            )}
            style={{ fontWeight: active ? 600 : 500 }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function OutcomesOverviewV2(): JSX.Element {
  const base = useTenantBase();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const addToast = useToastStore((s) => s.addToast);
  const isWideEnoughForGrid = useMediaQuery(`(min-width: ${GRID_MIN_WIDTH}px)`);

  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const role = useAuthStore((s) => s.role);
  const isAdmin = isSuperAdmin || role === "admin" || role === "owner";
  // A hiring manager may READ outcomes but never write them (the backend 403s
  // the PATCH). Render the cells read-only rather than letting them type into a
  // control whose save will bounce.
  const canEdit = role !== "hiring_manager";

  const me = useMe();
  const isInHouse = (me.data?.dashboard_persona ?? "in_house") === "in_house";

  // ── URL-held view state (shareable, and stable for the visual harness) ────
  const ownerParam = isAdmin ? searchParams.get("owner") : null;
  const groupParam = searchParams.get("fields");
  const group: LedgerColumnGroup =
    groupParam === "all" || groupParam === "agency" || groupParam === "employment"
      ? groupParam
      : // In-house tenants land on Employment; agencies work both groups daily,
        // so they get the full grid.
        isInHouse
        ? "employment"
        : "all";

  const sortParam = searchParams.get("sort");
  const sort: HiredLedgerSort =
    sortParam && SORT_KEYS.has(sortParam) ? (sortParam as HiredLedgerSort) : "hired";
  const direction: SortDirection = searchParams.get("dir") === "asc" ? "asc" : "desc";
  const needsData = searchParams.get("needs") === "1";

  const searchParamValue = searchParams.get("q") ?? "";
  const [searchDraft, setSearchDraft] = useState(searchParamValue);
  const [debouncedSearch, setDebouncedSearch] = useState(searchParamValue);

  // Debounce the wire call, not the input — the box stays responsive while the
  // server-side search settles.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchDraft), 250);
    return () => clearTimeout(t);
  }, [searchDraft]);

  const setParam = useCallback(
    (key: string, value: string | null): void => {
      const params = new URLSearchParams(searchParams);
      if (value === null || value === "") params.delete(key);
      else params.set(key, value);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const ledgerQ = useHiredLedger({
    owner: ownerParam,
    search: debouncedSearch,
    sort,
    direction,
    needsData,
  });
  const teamQ = useTeamUsers();
  const teamUsers = isAdmin ? (teamQ.data ?? []) : [];

  const page = ledgerQ.data;
  const items = useMemo(() => page?.items ?? [], [page]);
  const kpis = useMemo(() => computeKpis(items), [items]);
  const hasMore = page?.has_more ?? false;

  // ── Inline cell editing ──────────────────────────────────────────────────
  const patch = useLedgerCellPatch();
  const [cellErrors, setCellErrors] = useState<Record<string, string>>({});

  const onCellCommit = useCallback(
    (row: HiredLedgerItem, field: LedgerFieldKey, value: string | boolean | null): void => {
      const key = `${row.candidate_id}:${field}`;
      // Clear any previous failure on this cell — the user is retrying.
      setCellErrors((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });

      patch.mutate(
        { candidateId: row.candidate_id, jdId: row.jd_id, field, value },
        {
          onError: (err) => {
            // Never silently drop an edit: the optimistic value is rolled back
            // by the hook, the cell is flagged, and a toast says what happened.
            const message =
              err.status === 403
                ? "You don't have permission to edit this outcome."
                : err.status === 404
                  ? "That candidate is no longer in your list."
                  : "Couldn't save that change. It has been undone.";
            setCellErrors((prev) => ({ ...prev, [key]: message }));
            addToast(`${row.candidate_name}: ${message}`, "error");
          },
        },
      );
    },
    [patch, addToast],
  );

  const onSort = useCallback(
    (field: string): void => {
      if (!SORT_KEYS.has(field)) return;
      const params = new URLSearchParams(searchParams);
      if (field === sort) {
        params.set("dir", direction === "asc" ? "desc" : "asc");
      } else {
        params.set("sort", field);
        // Names read best A→Z; everything else reads best most-recent-first.
        params.set("dir", field === "candidate" || field === "role" ? "asc" : "desc");
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams, sort, direction],
  );

  const setOwner = (next: string): void => setParam("owner", next === "all" ? null : next);

  const isPending = ledgerQ.isPending;
  const isError = ledgerQ.isError;
  const isFiltered = debouncedSearch.trim() !== "" || needsData || !!ownerParam;
  const isEmpty = !isPending && !isError && items.length === 0;

  const trackedLabel = isPending
    ? null
    : `${kpis.tracked}${hasMore ? "+" : ""} hired candidate${kpis.tracked === 1 ? "" : "s"}` +
      (kpis.needsData > 0 ? ` · ${kpis.needsData} still need data` : "");

  const emptyState = (
    <span className="text-[13px] text-text-secondary">
      {isFiltered ? "No hired candidates match these filters." : "No hired candidates yet."}
    </span>
  );

  return (
    <div>
      {/* ── PAGE HEAD ────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-[14px]" style={{ marginBottom: "18px" }}>
        <div className="min-w-0 flex-1">
          <div
            className="text-[hsl(var(--brand,214_100%_41%))] font-bold uppercase"
            style={{ fontSize: "11px", letterSpacing: "0.08em" }}
          >
            Analytics · Post-placement
          </div>
          <h1
            className="text-text-primary"
            style={{ fontSize: "22px", fontWeight: 650, letterSpacing: "-0.02em", margin: 0 }}
          >
            Outcomes
          </h1>
          <div className="text-text-secondary" style={{ fontSize: "13.5px", marginTop: "3px" }}>
            {isPending ? (
              <Skeleton className="h-[14px] w-[320px]" data-testid="v2-outcomes-subtitle-loading" />
            ) : (
              <>Every hire, and how it turned out. {trackedLabel}</>
            )}
          </div>
        </div>

        {/* Entry to Screen 3 (model accuracy / calibration curve). Every role
            reaches it; the accuracy screen scopes its data server-side. */}
        <button
          type="button"
          onClick={() => navigate(`${base}/outcomes/accuracy`)}
          data-testid="v2-outcomes-accuracy-link"
          className={cn(
            "h-9 inline-flex items-center gap-[6px] transition-colors",
            "rounded-[var(--radius)] border border-border-default bg-surface-card",
            "text-text-secondary hover:text-text-primary",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus",
          )}
          style={{ padding: "0 12px", fontSize: "12.5px", fontWeight: 600 }}
        >
          Model accuracy
          <span aria-hidden="true">→</span>
        </button>

        {/* Admin-only "By recruiter" filter (?owner=). Non-admins never see it;
            the API scopes their data to themselves regardless. */}
        {isAdmin && (
          <div className="relative inline-flex" data-testid="v2-outcomes-owner-filter">
            <select
              value={ownerParam ?? "all"}
              onChange={(e) => setOwner(e.target.value)}
              aria-label="Filter outcomes by recruiter"
              className={cn(
                "h-9 appearance-none cursor-pointer transition-colors",
                "rounded-[var(--radius)] border border-border-default bg-surface-card",
                "text-text-secondary hover:text-text-primary",
              )}
              style={{ padding: "0 30px 0 12px", fontSize: "12.5px", fontWeight: 500 }}
            >
              <option value="all">All recruiters</option>
              {teamUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {`${u.first_name} ${u.last_name}`.trim() || u.email}
                </option>
              ))}
            </select>
            <svg
              className="pointer-events-none absolute text-text-tertiary"
              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </div>
        )}
      </div>

      {/* ── BODY ─────────────────────────────────────────────────────────── */}
      {isPending ? (
        <LedgerLoading />
      ) : isError ? (
        <StateCard
          testId="v2-outcomes-error"
          title="Couldn't load outcomes"
          body="Something went wrong fetching the hired candidates. Refresh to try again."
        />
      ) : isEmpty && !isFiltered ? (
        <StateCard
          testId="v2-outcomes-empty"
          title="No hired candidates yet"
          body="When you move a candidate to Hired, they appear here automatically with every outcome field ready to fill in — that's what turns our pre-hire fit verdicts into a measured accuracy number."
        />
      ) : (
        <>
          {/* KPI ROW — computed over the loaded page (see file header). */}
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[16px]"
            data-testid="v2-outcomes-kpi-row"
            style={{ marginBottom: "16px" }}
          >
            <KpiCard
              north
              label="Prediction accuracy"
              metric={pctStr(kpis.accuracy)}
              unit={kpis.accuracy == null ? undefined : "%"}
              delta={null}
              foot={
                kpis.gradedCount > 0
                  ? `Fit band held on ${kpis.gradedCount} graded hire${kpis.gradedCount === 1 ? "" : "s"}`
                  : "Awaiting the first graded 180-day outcome"
              }
              sparkPts={[]}
              sparkCvar="--chart-1"
            />
            <KpiCard
              label="Hires tracked"
              metric={`${kpis.tracked}${hasMore ? "+" : ""}`}
              delta={null}
              foot="Candidates in a hired state"
              sparkPts={[]}
              sparkCvar="--chart-2"
            />
            <KpiCard
              label="180-day retention"
              metric={pctStr(kpis.retention)}
              unit={kpis.retention == null ? undefined : "%"}
              delta={null}
              foot="Still employed at 180 days"
              sparkPts={[]}
              sparkCvar="--chart-3"
            />
            <KpiCard
              label="Would-rehire rate"
              metric={pctStr(kpis.rehire)}
              unit={kpis.rehire == null ? undefined : "%"}
              delta={null}
              foot="Manager confirmed at the 180-day check-in"
              sparkPts={[]}
              sparkCvar="--chart-5"
            />
          </div>

          {/* TOOLBAR — search · column groups · needs-data worklist filter */}
          <div
            className="flex flex-wrap items-center gap-[10px]"
            style={{ marginBottom: "12px" }}
            data-testid="v2-outcomes-toolbar"
          >
            <input
              type="search"
              value={searchDraft}
              onChange={(e) => {
                setSearchDraft(e.target.value);
                setParam("q", e.target.value);
              }}
              placeholder="Search hired candidates or roles"
              aria-label="Search hired candidates or roles"
              data-testid="v2-outcomes-search"
              className={cn(
                "h-9 min-w-[220px] flex-1 rounded-[var(--radius)] border border-border-default",
                "bg-surface-card text-text-primary placeholder:text-text-tertiary",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus",
              )}
              style={{ padding: "0 12px", fontSize: "12.5px", maxWidth: "360px" }}
            />

            {isWideEnoughForGrid && (
              <GroupToggle
                value={group}
                onChange={(v) => setParam("fields", v)}
                deEmphasiseAgency={isInHouse}
              />
            )}

            <button
              type="button"
              onClick={() => setParam("needs", needsData ? null : "1")}
              aria-pressed={needsData}
              data-testid="v2-outcomes-needs-data"
              className={cn(
                "h-9 inline-flex items-center gap-[6px] rounded-[var(--radius)] border",
                "px-[12px] text-[12.5px] transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus",
                needsData
                  ? "border-border-default bg-surface-subtle text-text-primary"
                  : "border-border-default bg-surface-card text-text-secondary hover:text-text-primary",
              )}
              style={{ fontWeight: needsData ? 600 : 500 }}
            >
              Needs data
              {kpis.needsData > 0 && !needsData && (
                <span className="tabular-nums text-text-tertiary">{kpis.needsData}</span>
              )}
            </button>
          </div>

          {isWideEnoughForGrid ? (
            <HiredLedgerGrid
              rows={items}
              group={group}
              sort={sort}
              direction={direction}
              onSort={onSort}
              onCellCommit={onCellCommit}
              cellErrors={cellErrors}
              base={base}
              canEdit={canEdit}
              emptyState={emptyState}
            />
          ) : (
            <HiredLedgerStack
              rows={items}
              group={group}
              onCellCommit={onCellCommit}
              cellErrors={cellErrors}
              base={base}
              canEdit={canEdit}
              emptyState={emptyState}
            />
          )}

          <p className="text-text-tertiary" style={{ fontSize: "12px", marginTop: "12px" }}>
            {hasMore
              ? `Showing the ${items.length} most recent hires. KPIs above cover this page.`
              : "Click any cell to fill it in — changes save when you move on."}
          </p>
        </>
      )}

      {/* Page-ready marker for the visual harness (renders once loading settles). */}
      {!isPending && <div data-testid="page-ready" aria-hidden="true" className="sr-only" />}
    </div>
  );
}
